package com.htmlagent.site.components.sections.landing

import androidx.compose.runtime.Composable
import com.htmlagent.site.components.sections.Glyph
import com.htmlagent.site.components.widgets.reveal
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.theme.AudienceCardStyle
import com.htmlagent.site.theme.CardArrowStyle
import com.htmlagent.site.theme.CardBodyStyle
import com.htmlagent.site.theme.CardLinkStyle
import com.htmlagent.site.theme.CardTitleStyle
import com.htmlagent.site.theme.IconTileStyle
import com.htmlagent.site.theme.Icons
import com.htmlagent.site.theme.StatGridStyle
import com.htmlagent.site.theme.Tokens
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** One kind of page somebody comes here to ask for. */
private data class Audience(
    val icon: String,
    val title: String,
    val body: String,
    val link: String,
)

/**
 * Who it is for, as four things you could ask for tomorrow.
 *
 * Each card ends in its own link and they all lead to the same place, which is deliberate: the
 * page has one door, and four ways of recognising yourself before you walk through it.
 */
@Composable
fun LandingAudience() {
    val t = LocalStrings.current
    val cards = listOf(
        Audience(
            Icons.Storefront,
            t.landing.audienceLocalTitle,
            t.landing.audienceLocalBody,
            t.landing.audienceLocalLink,
        ),
        Audience(
            Icons.Frame,
            t.landing.audiencePortfolioTitle,
            t.landing.audiencePortfolioBody,
            t.landing.audiencePortfolioLink,
        ),
        Audience(
            Icons.Calendar,
            t.landing.audienceEventTitle,
            t.landing.audienceEventBody,
            t.landing.audienceEventLink,
        ),
        Audience(
            Icons.Rocket,
            t.landing.audienceLaunchTitle,
            t.landing.audienceLaunchBody,
            t.landing.audienceLaunchLink,
        ),
    )

    Band(SECTION_AUDIENCE) {
        SectionHead(
            t.landing.audienceEyebrow,
            t.landing.audienceTitle,
            headingId = headingId(SECTION_AUDIENCE),
        )

        Div(StatGridStyle.toModifier().toAttrs()) {
            cards.forEachIndexed { position, card ->
                Div(AudienceCardStyle.toModifier().reveal(position * STAGGER_MS).toAttrs()) {
                    // The card turns over to the accent under the pointer; each part says
                    // which it is so the card's own rule can recolour it. See AudienceCardStyle.
                    Span(IconTileStyle.toModifier().toAttrs { attr("data-part", "tile") }) {
                        Glyph(card.icon, Tokens.iconLg.value())
                    }
                    H3(CardTitleStyle.toModifier().toAttrs { attr("data-part", "title") }) {
                        Text(card.title)
                    }
                    P(CardBodyStyle.toModifier().toAttrs { attr("data-part", "body") }) {
                        Text(card.body)
                    }
                    A(
                        href = "#$SECTION_CTA",
                        attrs = CardLinkStyle.toModifier().toAttrs { attr("data-part", "link") },
                    ) {
                        Text(card.link)
                        Span(CardArrowStyle.toModifier().toAttrs { attr("data-arrow", "") }) {
                            Glyph(Icons.ArrowRight, Tokens.iconSm.value())
                        }
                    }
                }
            }
        }
    }
}
